import { FormEvent, useEffect, useState } from 'react';
import type { AppSettings, PaymentMethod } from '@rosita/shared';
import { SEO } from '../../seo/useSEO';
import { api, extractError } from '../../lib/api';
import { useToast } from '../../components/admin/Toast';

const primaryBtn =
  'rounded-md bg-admin-primary px-4 py-2 text-sm font-medium text-white hover:bg-admin-primary-hover disabled:opacity-50';
const textareaClass =
  'w-full rounded-md border border-admin-border bg-white px-3 py-2 text-sm focus:border-admin-primary focus:outline-none';

// The methods actually shown at checkout. `bank_transfer` is an unused stub.
const PAYMENT_METHOD_OPTIONS: { id: PaymentMethod; label: string; hint: string }[] = [
  { id: 'mbway', label: 'MB WAY', hint: 'Pagamento por telemóvel via app MB WAY.' },
  { id: 'multibanco', label: 'Multibanco', hint: 'Referência multibanco (entidade + referência).' },
  { id: 'credit_card', label: 'Cartão de crédito', hint: 'Visa, Mastercard e Maestro.' },
];

export default function AdminSiteSettingsPage(): JSX.Element {
  const toast = useToast();
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [enabledPaymentMethods, setEnabledPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadSettings = async (): Promise<void> => {
    const res = await api.get<AppSettings>('/admin/settings');
    setMaintenanceMode(res.data.maintenanceMode ?? false);
    setMaintenanceMessage(res.data.maintenanceMessage ?? '');
    setEnabledPaymentMethods(res.data.enabledPaymentMethods ?? []);
  };

  useEffect(() => {
    void (async () => {
      try {
        await loadSettings();
      } catch (err) {
        toast.error(extractError(err, 'Falha ao carregar configurações'));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const togglePaymentMethod = (id: PaymentMethod, on: boolean): void => {
    setEnabledPaymentMethods((prev) =>
      on ? [...new Set([...prev, id])] : prev.filter((m) => m !== id)
    );
  };

  const handleSave = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (enabledPaymentMethods.length === 0) {
      toast.error('Tens de manter pelo menos um método de pagamento ativo.');
      return;
    }
    setSaving(true);
    try {
      const res = await api.put<AppSettings>('/admin/settings', {
        maintenanceMode,
        maintenanceMessage: maintenanceMessage.trim() === '' ? null : maintenanceMessage.trim(),
        enabledPaymentMethods,
      });
      setMaintenanceMode(res.data.maintenanceMode ?? false);
      setMaintenanceMessage(res.data.maintenanceMessage ?? '');
      setEnabledPaymentMethods(res.data.enabledPaymentMethods ?? []);
      toast.success('Definições guardadas.');
    } catch (err) {
      toast.error(extractError(err, 'Falha ao guardar definições'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-admin-text-soft">A carregar…</p>;

  return (
    <>
      <SEO title="Definições do site" noindex />
      <h1>Definições do site</h1>
      <p className="mt-1 mb-6 text-admin-text-soft">
        Controlos gerais da loja. Usa com cuidado — algumas opções afetam todos os visitantes.
      </p>

      <form onSubmit={handleSave} className="max-w-2xl">
        <section className="rounded-lg border border-admin-border bg-white p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="mb-1">Modo de manutenção</h2>
              <p className="text-sm text-admin-text-soft">
                Quando está <strong>ativo</strong>, a loja fica escondida de todos os visitantes
                — só a equipa (com sessão iniciada) consegue navegar no site. Os visitantes veem
                uma página temporária.
              </p>
            </div>
            <label className="inline-flex shrink-0 cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={maintenanceMode}
                onChange={(e) => setMaintenanceMode(e.target.checked)}
                className="h-5 w-5"
              />
              <span className="text-sm font-medium">{maintenanceMode ? 'Ativo' : 'Inativo'}</span>
            </label>
          </div>

          <div className="mt-5">
            <label htmlFor="maintenance-message" className="mb-1 block text-sm font-medium">
              Mensagem da página de manutenção
            </label>
            <p className="mb-2 text-sm text-admin-text-soft">
              Texto mostrado aos visitantes enquanto o modo de manutenção estiver ativo. Deixar em
              branco para usar a mensagem por defeito.
            </p>
            <textarea
              id="maintenance-message"
              value={maintenanceMessage}
              onChange={(e) => setMaintenanceMessage(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Estamos a preparar novidades para ti…"
              className={textareaClass}
            />
          </div>
        </section>

        <section className="mt-6 rounded-lg border border-admin-border bg-white p-6">
          <h2 className="mb-1">Métodos de pagamento</h2>
          <p className="mb-4 text-sm text-admin-text-soft">
            Escolhe que métodos aparecem ao cliente no checkout. Os métodos desativados deixam de
            estar disponíveis de imediato. Tens de manter pelo menos um ativo.
          </p>
          <div className="flex flex-col gap-3">
            {PAYMENT_METHOD_OPTIONS.map((opt) => {
              const checked = enabledPaymentMethods.includes(opt.id);
              return (
                <label
                  key={opt.id}
                  className="flex items-start justify-between gap-4 rounded-md border border-admin-border px-4 py-3"
                >
                  <span>
                    <span className="block text-sm font-medium">{opt.label}</span>
                    <span className="block text-sm text-admin-text-soft">{opt.hint}</span>
                  </span>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => togglePaymentMethod(opt.id, e.target.checked)}
                    className="mt-0.5 h-5 w-5 shrink-0"
                  />
                </label>
              );
            })}
          </div>
        </section>

        <div className="mt-4">
          <button type="submit" disabled={saving} className={primaryBtn}>
            {saving ? 'A guardar…' : 'Guardar'}
          </button>
        </div>
      </form>
    </>
  );
}
